import { Dispatch, SetStateAction, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { handleErrors } from '../../../../utils/errors';
import { GmailPopModel } from '../../../../../types';
import SharedDialog from '../../../../shared/shared-dialog';
import { UserKeys, bulkSetPop } from '../../../../api/users-api';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import { useStore } from '../../../state/zustand-store';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import * as analytics from '../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../shared/analytics/module-list';
import {
  UserRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
interface PopSettingsProps {
  open: boolean;
  handleClose: () => void;
  selectedUsers: string[];
  setSelectedUsers: Dispatch<SetStateAction<string[]>>;
  changeLogRoles: string[];
}

export default function PopSettingsDialog({
  open,
  handleClose,
  selectedUsers,
  setSelectedUsers,
  changeLogRoles,
}: PopSettingsProps) {
  const { createEvent } = useAnalytics();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const [enablePopForValue, setEnablePopForValue] = useState('ALL_MAIL');
  const [actionOptionsValue, setActionOptionsValue] = useState('KEEP');
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();

  const { mutate, isPending: isSaving } = useMutation({
    mutationFn: () =>
      bulkSetPop(
        'User',
        selectedUsers,
        {
          enable: true,
          enableFor: enablePopForValue,
          action: actionOptionsValue,
        } as GmailPopModel,
        changeLogMessage
      ),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'info',
        message: 'The task to update the user(s) POP settings has started.',
      });
      selectedUsers.forEach((user) => {
        queryClient.invalidateQueries({
          queryKey: [UserKeys.GmailPopSettings, user],
        });
      });
      setChangeLogMessage('');
      setEnablePopForValue('ALL_MAIL');
      setActionOptionsValue('KEEP');
      setSelectedUsers([]);
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue updating the user(s) POP settings. Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(analytics.user_management.user_grid.bulk_set_pop_settings, {
        module: ModuleList.modules.userManagement.name,
        section: ModuleList.modules.userManagement.name,
      });
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    if (isAbleToSeeUIElement(changeLogRoles, UserRoles.BulkSetPop.value)) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  const enablePopFor = [
    { value: 'ALL_MAIL', displayValue: 'All Mail' },
    { value: 'MAIL_FROM_NOW_ON', displayValue: 'Mail from now on' },
  ];

  const actionOptions = [
    { value: 'KEEP', displayValue: `Keep Gmail's copy in the inbox` },
    { value: 'MARK_READ', displayValue: `Mark Gmail's copy as read` },
    { value: 'ARCHIVE', displayValue: `Archive Gmail's copy` },
    { value: 'DELETE', displayValue: `Delete Gmail's copy` },
  ];

  return (
    <SharedDialog
      open={open}
      handleClose={() => {
        handleClose();
        setEnablePopForValue('ALL_MAIL');
        setActionOptionsValue('KEEP');
      }}
      title="Enable POP"
      dialogContent={
        <Stack gap={2}>
          <Typography>
            {selectedUsers.length > 1 ? (
              <>
                Enable POP settings for{' '}
                <strong>{selectedUsers.length} users</strong>.
              </>
            ) : (
              <>
                Enable POP settings for <strong>{selectedUsers[0]}</strong>.
              </>
            )}
          </Typography>
          <TextField
            autoFocus
            select
            value={enablePopForValue}
            onChange={(event) => setEnablePopForValue(event.target.value)}
            fullWidth
          >
            {enablePopFor.map((option) => (
              <MenuItem value={option.value} key={option.value}>
                {option.displayValue}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            value={actionOptionsValue}
            onChange={(event) => setActionOptionsValue(event.target.value)}
            fullWidth
          >
            {actionOptions.map((option) => (
              <MenuItem value={option.value} key={option.value}>
                {option.displayValue}
              </MenuItem>
            ))}
          </TextField>
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isSaving}
          />
        </Stack>
      }
      primaryButton={{
        loading: isSaving,
        action: handleSubmit,
        disabled: isSaving,
        title: 'Enable POP',
      }}
    />
  );
}
